import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  Divider,
  Chip,
  useTheme,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  FilterAlt as FilterIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  Person as DoctorIcon,
  CalendarMonth as DateIcon,
  Schedule as TimeIcon,
} from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ja } from 'date-fns/locale';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import type { DoctorShiftFilterOptions } from '../../types/doctorShift';
import { doctorApiService } from '../../services/doctorApiService';

/** プルダウン用の医師選択肢（doctor_code がフィルター送信値） */
interface DoctorOption {
  code: string;
  name: string;
}

interface DoctorShiftFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  filters: DoctorShiftFilterOptions;
  onFiltersChange: (filters: DoctorShiftFilterOptions) => void;
  onApply: () => void;
  onReset: () => void;
}

/**
 * 医師シフトフィルタードロワーコンポーネント
 * 
 * 意図：
 * - 高度な検索・フィルタリング機能の提供
 * - 直感的なUI/UX
 * - フィルター条件の可視化
 * 
 * 注意点：
 * - フィルター状態の同期
 * - パフォーマンス最適化
 * 
 * 改善提案：
 * - フィルター条件の保存機能
 * - プリセットフィルター
 * - 高度な日付範囲選択
 */
export const DoctorShiftFilterDrawer: React.FC<DoctorShiftFilterDrawerProps> = ({
  open,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onReset,
}) => {
  const theme = useTheme();
  
  // ローカル状態（適用前の一時的なフィルター）
  const [localFilters, setLocalFilters] = useState<DoctorShiftFilterOptions>(filters);

  // 医師プルダウンの選択肢（doctorsテーブル由来）
  const [doctorOptions, setDoctorOptions] = useState<DoctorOption[]>([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const doctorsFetchStartedRef = useRef(false);

  // 初回オープン時に医師一覧を全件取得（per_page上限100のためページを跨いで収集）
  // 注意: 取得状態を依存配列に入れると開始直後に effect が再実行されてしまうため、
  //       実行済み管理は ref で行い、依存は open のみとする。
  useEffect(() => {
    if (!open || doctorsFetchStartedRef.current) return;
    doctorsFetchStartedRef.current = true;
    (async () => {
      setDoctorsLoading(true);
      try {
        const options: DoctorOption[] = [];
        let page = 1;
        let lastPage = 1;
        do {
          const res = await doctorApiService.getDoctors({ page, per_page: 100 });
          res.data.forEach((d) => {
            options.push({ code: d.doctor_code, name: `${d.first_name} ${d.last_name}` });
          });
          lastPage = res.meta?.last_page ?? 1;
          page += 1;
        } while (page <= lastPage);
        setDoctorOptions(options);
      } catch {
        // 取得失敗時は空のまま。次回オープン時に再試行できるようにする
        doctorsFetchStartedRef.current = false;
      } finally {
        setDoctorsLoading(false);
      }
    })();
  }, [open]);

  // 選択中の医師（doctor_ids = doctor_code の配列）
  const selectedDoctors = doctorOptions.filter(
    (o) => localFilters.doctor_ids?.includes(o.code),
  );

  // フィルターが変更されたらローカル状態を更新
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // ローカルフィルターの更新
  const updateLocalFilter = (key: keyof DoctorShiftFilterOptions, value: any) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };

  // フィルター適用
  const handleApply = () => {
    onFiltersChange(localFilters);
    onApply();
    onClose();
  };

  // フィルターリセット
  const handleReset = () => {
    setLocalFilters({
      page: 1,
      page_size: filters.page_size || 10,
      sort_by: 'shift_date',
      sort_order: 'desc'
    });
    onReset();
  };

  // アクティブフィルター数の計算
  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters.search) count++;
    if (localFilters.start_date) count++;
    if (localFilters.end_date) count++;
    if (localFilters.time_range_start) count++;
    if (localFilters.time_range_end) count++;
    if (localFilters.doctor_ids?.length) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: '90vw',
            borderRadius: '8px 0 0 8px',
            boxShadow: `0 0 20px ${alpha(theme.palette.common.black, 0.1)}`,
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* ヘッダー */}
          <Box
            sx={{
              p: 3,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FilterIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  フィルター
                </Typography>
                {activeFilterCount > 0 && (
                  <Chip
                    label={activeFilterCount}
                    size="small"
                    color="primary"
                    sx={{ minWidth: 24, height: 24 }}
                  />
                )}
              </Box>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* フィルター内容 */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            <Stack spacing={3}>
              {/* 医師で絞り込み（doctorsテーブルから名前を選択） */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DoctorIcon fontSize="small" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      医師で絞り込み
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Autocomplete
                    multiple
                    options={doctorOptions}
                    value={selectedDoctors}
                    loading={doctorsLoading}
                    loadingText="医師一覧を読み込み中..."
                    noOptionsText="該当する医師がいません"
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(o, v) => o.code === v.code}
                    onChange={(_, values) =>
                      updateLocalFilter(
                        'doctor_ids',
                        values.length ? values.map((v) => v.code) : undefined,
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={selectedDoctors.length ? '' : '医師名を選択...'}
                        InputProps={{
                          ...params.InputProps,
                          sx: { borderRadius: 2 },
                          endAdornment: (
                            <>
                              {doctorsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </AccordionDetails>
              </Accordion>

              {/* 日付範囲フィルター */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DateIcon fontSize="small" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      シフト日付
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <DatePicker
                      label="開始日"
                      value={localFilters.start_date ? new Date(localFilters.start_date) : null}
                      onChange={(date) => updateLocalFilter('start_date', date ? format(date, 'yyyy-MM-dd') : undefined)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          InputProps: {
                            sx: { borderRadius: 2 }
                          }
                        }
                      }}
                    />
                    <DatePicker
                      label="終了日"
                      value={localFilters.end_date ? new Date(localFilters.end_date) : null}
                      onChange={(date) => updateLocalFilter('end_date', date ? format(date, 'yyyy-MM-dd') : undefined)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          InputProps: {
                            sx: { borderRadius: 2 }
                          }
                        }
                      }}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>

              {/* 時間帯フィルター */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimeIcon fontSize="small" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      勤務時間帯
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <TimePicker
                      label="開始時間（以降）"
                      ampm={false}
                      value={localFilters.time_range_start ? new Date(`2000-01-01T${localFilters.time_range_start}`) : null}
                      onChange={(time) => updateLocalFilter('time_range_start', time ? format(time, 'HH:mm') : undefined)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          InputProps: {
                            sx: { borderRadius: 2 }
                          }
                        }
                      }}
                    />
                    <TimePicker
                      label="終了時間（以前）"
                      ampm={false}
                      value={localFilters.time_range_end ? new Date(`2000-01-01T${localFilters.time_range_end}`) : null}
                      onChange={(time) => updateLocalFilter('time_range_end', time ? format(time, 'HH:mm') : undefined)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          InputProps: {
                            sx: { borderRadius: 2 }
                          }
                        }
                      }}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>

              {/* アクティブフィルター表示 */}
              <AnimatePresence>
                {activeFilterCount > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                      適用中のフィルター
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {localFilters.search && (
                        <Chip
                          label={`検索: ${localFilters.search}`}
                          onDelete={() => updateLocalFilter('search', undefined)}
                          size="small"
                        />
                      )}
                      {!!localFilters.doctor_ids?.length && (
                        <Chip
                          label={`医師: ${selectedDoctors.map((o) => o.name).join(', ') || localFilters.doctor_ids.join(', ')}`}
                          onDelete={() => updateLocalFilter('doctor_ids', undefined)}
                          size="small"
                        />
                      )}
                      {localFilters.start_date && (
                        <Chip
                          label={`開始日: ${localFilters.start_date}`}
                          onDelete={() => updateLocalFilter('start_date', undefined)}
                          size="small"
                        />
                      )}
                      {localFilters.end_date && (
                        <Chip
                          label={`終了日: ${localFilters.end_date}`}
                          onDelete={() => updateLocalFilter('end_date', undefined)}
                          size="small"
                        />
                      )}
                      {localFilters.time_range_start && (
                        <Chip
                          label={`開始時間: ${localFilters.time_range_start}以降`}
                          onDelete={() => updateLocalFilter('time_range_start', undefined)}
                          size="small"
                        />
                      )}
                      {localFilters.time_range_end && (
                        <Chip
                          label={`終了時間: ${localFilters.time_range_end}以前`}
                          onDelete={() => updateLocalFilter('time_range_end', undefined)}
                          size="small"
                        />
                      )}
                    </Stack>
                  </motion.div>
                )}
              </AnimatePresence>
            </Stack>
          </Box>

          {/* フッターアクション */}
          <Box
            sx={{
              p: 3,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              backgroundColor: alpha(theme.palette.background.default, 0.5),
            }}
          >
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<ClearIcon />}
                sx={{ flex: 1, borderRadius: 2 }}
              >
                リセット
              </Button>
              <Button
                variant="contained"
                onClick={handleApply}
                startIcon={<FilterIcon />}
                sx={{ flex: 2, borderRadius: 2 }}
              >
                フィルター適用
              </Button>
            </Stack>
          </Box>
        </Box>
      </Drawer>
    </LocalizationProvider>
  );
};